import React, { useRef, useState, useEffect } from "react";
import { Box, IconButton, TextField, Typography, CircularProgress } from "@mui/material";
import { uploadImageToUrl } from "../components/dataProvider";
import CloseIcon from "@mui/icons-material/Close";

const DEFAULT_IMAGE =
  "https://te-ayudo-img.s3.us-east-2.amazonaws.com/test/users/683a58551632fe88f64db2e2@1756357057194";

const PLACEHOLDER_SVG =
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400'>
       <rect width='100%' height='100%' fill='#f0f0f0'/>
       <text x='50%' y='50%' alignment-baseline='middle' text-anchor='middle'
             fill='#9e9e9e' font-family='Arial' font-size='16'>Sin imagen</text>
     </svg>`
  );

const FakeServiceRow = ({ service, onChange, onDelete }) => {
  const [loading, setLoading] = useState(false);

  // capa base: lo que se ve “sí o sí” (placeholder o preview local)
  const [baseSrc, setBaseSrc] = useState((service.image || "").trim() || DEFAULT_IMAGE);

  // capa superior: URL remota (S3) + control de visibilidad
  const [showRemote, setShowRemote] = useState(false);
  const [remoteRev, setRemoteRev] = useState(0); // cache-buster/forzar remount

  const inputRef = useRef();

  // Cuando cambie la url del servicio desde el padre, intentamos mostrarla arriba
  useEffect(() => {
    const nextRemote = (service.image || "").toString().trim();
    if (nextRemote) {
      setShowRemote(false); // hasta que onLoad confirme
      setRemoteRev((r) => r + 1); // fuerza remount de la <img> remota
    }
  }, [service.image]);

  // Si la remota ya cargó, podemos “liberar” el baseSrc si era un ObjectURL local
  useEffect(() => {
    if (showRemote && baseSrc.startsWith("blob:")) {
      URL.revokeObjectURL(baseSrc);
      setBaseSrc(DEFAULT_IMAGE); // deja default detrás
    }
  }, [showRemote, baseSrc]);

  const handleImageClick = () => {
    if (!loading) inputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);

    // 1) Previsualiza local inmediatamente en la capa base
    const localUrl = URL.createObjectURL(file);
    setBaseSrc(localUrl);
    setShowRemote(false);

    try {
      // 2) Sube y luego coloca la URL remota en el padre
      const url = await uploadImageToUrl(file);
      // cache-buster por si el navegador es agresivo con caché en URLs sin extensión
      const busted = url + (url.includes("?") ? "&" : "?") + "v=" + Date.now();
      onChange(service.id, { ...service, image: busted });
    } finally {
      setLoading(false);
      e.target.value = ""; // poder volver a elegir el mismo archivo
    }
  };

  // Fuente base (placeholder o preview local o la que viniera)
  const fallbackBase = baseSrc || DEFAULT_IMAGE;

  // Fuente remota (solo si hay service.image)
  const remoteClean = (service.image || "").trim();
  const remoteKey = `${service.id}-${remoteRev}`; // remount garantizado de la capa superior

  return (
    <Box
      sx={{
        display: "flex",
        border: "1px solid #e0e0e0",
        borderRadius: 3,
        mb: 3,
        boxShadow: 2,
        overflow: "hidden",
        position: "relative",
        minHeight: 180,
        background: "#fff",
      }}
    >
      <IconButton
        size="small"
        color="error"
        onClick={() => onDelete(service.id)}
        sx={{
          position: "absolute",
          top: 2,
          right: 4,
          zIndex: 3,
          p: 0.5,
          "&:hover": { background: "rgba(255,255,255,1)" },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {/* Columna imagen */}
      <Box sx={{ width: 180, flexShrink: 0 }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "1 / 1",
            overflow: "hidden",
            bgcolor: "#f7f7f7",
            borderRight: "1px solid #e0e0e0",
            cursor: loading ? "wait" : "pointer",
          }}
          onClick={handleImageClick}
        >
          {/* INPUT FILE */}
          <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleFileChange} disabled={loading} />

          {/* Capa base (placeholder o preview local) */}
          <img
            src={fallbackBase || PLACEHOLDER_SVG}
            alt={service.name}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              display: "block",
              filter: showRemote ? "blur(0px)" : "none",
            }}
            onError={() => {
              // si incluso el base falla, usa SVG placeholder
              if (fallbackBase !== PLACEHOLDER_SVG) setBaseSrc(PLACEHOLDER_SVG);
            }}
          />

          {/* Capa superior (URL remota). Solo se pinta si existe una URL remota */}
          {remoteClean && (
            <img
              key={remoteKey}
              src={remoteClean}
              alt={service.name}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
                opacity: showRemote ? 1 : 0,
                transition: "opacity 200ms ease",
                zIndex: 2,
                pointerEvents: "none", // para que el click pase al contenedor
              }}
              onLoad={() => setShowRemote(true)}
              onError={() => setShowRemote(false)}
            />
          )}

          {loading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(255,255,255,0.5)",
                zIndex: 4,
              }}
            >
              <CircularProgress size={40} />
            </Box>
          )}
        </Box>
      </Box>

      {/* Form a la derecha */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", p: 3 }}>
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            label="Nombre"
            value={service.name}
            onChange={(e) => onChange(service.id, { ...service, name: e.target.value })}
            size="small"
            fullWidth
          />
          <TextField
            label="Precio (Bs.)"
            type="number"
            value={service.price}
            onChange={(e) => onChange(service.id, { ...service, price: e.target.value })}
            size="small"
            sx={{ maxWidth: 180 }}
          />
        </Box>
        <TextField
          label="Descripción"
          value={service.description}
          onChange={(e) => onChange(service.id, { ...service, description: e.target.value })}
          size="small"
          fullWidth
          multiline
          minRows={2}
        />
      </Box>
    </Box>
  );
};

export default FakeServiceRow;
